/**
 * Voertuigwijziging-melding (FO-116-B, batch-22) — gedeeld tussen API (DTO +
 * Zod-input) en de portal/backoffice-UI.
 *
 * De bestuurder meldt vanuit de medewerker-app schade, een onderhoudsbeurt of
 * een gegevenswijziging aan ZIJN voertuig. De melding muteert de Voertuig-record
 * NIET; de fleet-/planner-inbox in de backoffice handelt af via status +
 * opvolgingsnotitie. Spiegelt het MeldingOnveiligeSituatie-patroon.
 *
 * Gate-keys: portal (eigen bestuurder) = `VOERTUIGEN` canView + portal-access;
 * backoffice-afhandeling = `VOERTUIGEN` canEdit.
 */

import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Enums (string-unions, gelijk aan de Prisma-enums) ───────────────────────

export const VOERTUIG_WIJZIGING_TYPE = ['SCHADE', 'ONDERHOUD', 'GEGEVENS', 'OVERIG'] as const
export type VoertuigWijzigingType = (typeof VOERTUIG_WIJZIGING_TYPE)[number]

export const VOERTUIG_MELDING_STATUS = [
  'INGEDIEND',
  'IN_BEHANDELING',
  'AFGEHANDELD',
  'GESLOTEN',
] as const
export type VoertuigMeldingStatus = (typeof VOERTUIG_MELDING_STATUS)[number]

// Terminale statussen — bij overgang hiernaartoe wordt afgehandeldDoor/-Op gezet.
export const VOERTUIG_MELDING_TERMINALE_STATUS: ReadonlyArray<VoertuigMeldingStatus> = [
  'AFGEHANDELD',
  'GESLOTEN',
]

// ─── Labels (NL-talig voor UI) ───────────────────────────────────────────────

export const VOERTUIG_WIJZIGING_TYPE_LABELS: Record<VoertuigWijzigingType, string> = {
  SCHADE: 'Schade',
  ONDERHOUD: 'Onderhoud',
  GEGEVENS: 'Gegevenswijziging',
  OVERIG: 'Overig',
}

export const VOERTUIG_MELDING_STATUS_LABELS: Record<VoertuigMeldingStatus, string> = {
  INGEDIEND: 'Ingediend',
  IN_BEHANDELING: 'In behandeling',
  AFGEHANDELD: 'Afgehandeld',
  GESLOTEN: 'Gesloten',
}

// ─── DTO ─────────────────────────────────────────────────────────────────────

export interface VoertuigWijzigingMeldingDTO {
  id: string
  voertuigId: string
  /** Kenteken van het gemelde voertuig (leesbaar in de inbox). */
  kenteken: string | null
  employeeId: string
  /** Naam-resolved melder, of null. */
  melderNaam: string | null
  type: VoertuigWijzigingType
  omschrijving: string
  /**
   * Aantal geüploade foto's. Bewust GEEN rauwe storage-keys naar de client: de
   * UI streamt elke foto via het authed, ownership-gecheckte `/foto/:index`-
   * endpoint (index 0..fotoCount-1). Voorkomt het lekken van de interne
   * blob-padstructuur (review N3).
   */
  fotoCount: number
  status: VoertuigMeldingStatus
  ingediendOp: string
  opvolgingNotitie: string | null
  afgehandeldDoorId: string | null
  afgehandeldDoorNaam: string | null
  afgehandeldOp: string | null
  createdAt: string
  updatedAt: string
}

// ─── Zod request-body schemas + input types ──────────────────────────────────

// Create: de monteur kiest alleen type + omschrijving; de voertuigId wordt
// server-side uit zijn actieve toewijzing afgeleid (nooit uit de body).
export const createVoertuigWijzigingMeldingSchema = z.object({
  type: z.enum(VOERTUIG_WIJZIGING_TYPE),
  omschrijving: z.string().trim().min(5).max(4000),
})
export type CreateVoertuigWijzigingMeldingInput = z.infer<
  typeof createVoertuigWijzigingMeldingSchema
>

export const updateVoertuigWijzigingMeldingSchema = z
  .object({
    status: z.enum(VOERTUIG_MELDING_STATUS).optional(),
    opvolgingNotitie: z.string().trim().max(4000).nullable().optional(),
  })
  .refine((v) => v.status !== undefined || v.opvolgingNotitie !== undefined, {
    message: 'Geef minimaal een status of opvolgingsnotitie mee',
  })
export type UpdateVoertuigWijzigingMeldingInput = z.infer<
  typeof updateVoertuigWijzigingMeldingSchema
>

export const listVoertuigWijzigingMeldingQuerySchema = z.object({
  status: z.enum(VOERTUIG_MELDING_STATUS).optional(),
  type: z.enum(VOERTUIG_WIJZIGING_TYPE).optional(),
  voertuigId: idParam().optional(),
})
export type ListVoertuigWijzigingMeldingQuery = z.infer<
  typeof listVoertuigWijzigingMeldingQuerySchema
>
